import { useEffect, useState } from "react";
import api from "../api";

const money = (n) => "$" + Math.round(n || 0).toLocaleString();

const SOURCE_LABELS = {
  inquiry: "Inquiry", outreach: "Outreach", newsletter: "Newsletter",
  lead_magnet: "Lead magnet", manual: "Manual",
};

const DIM = "var(--text-dim,#8b97a7)";
const LINE = "1px solid var(--line,#23272f)";
const cell = { padding: "8px 10px", borderBottom: LINE, whiteSpace: "nowrap" };
const num = { ...cell, textAlign: "right", fontVariantNumeric: "tabular-nums" };
const link = { color: "#3b82f6" };

function Headline({ label, value, note }) {
  return (
    <div style={{ border: "1px solid var(--line,#2a2f3a)", borderRadius: 10, padding: "10px 14px", minWidth: 150 }}>
      <div style={{ fontSize: 20, fontWeight: 700 }}>{value}</div>
      <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".04em", color: DIM }}>{label}</div>
      {note && <div style={{ fontSize: 12, color: DIM, marginTop: 2 }}>{note}</div>}
    </div>
  );
}

// What HubSpot holds as open deals, read from the copy stored after each sync.
// It does not follow the page's date filter: an open deal is open whenever it
// was made. `refreshKey` changes when a sync finishes, which re-reads the copy.
export default function OpenPipelinePanel({ refreshKey }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    let current = true;
    api.get("/pipeline/open").then(({ data }) => { if (current) setData(data); }).catch(() => {});
    return () => { current = false; };
  }, [refreshKey]);

  if (!data || data.empty) return null;

  const sources = Object.entries(data.by_source || {}).filter(([, s]) => s.count > 0);
  const needsLook = data.needs_a_look || [];
  const quiet = data.gone_quiet || [];
  const stages = data.stages || [];
  const deals = stages.reduce((n, s) => n + (s.count || 0), 0);
  const blank = stages.reduce((n, s) => n + (s.blank || 0), 0);
  // Missing until the first sync after this shipped stores it.
  const ds = data.ds && data.ds.count > 0 ? data.ds : null;
  // Everything invoiced at the margin known from deals with a "DS net revenue".
  // Missing until a sync after this shipped, or while no deal has a net.
  const est = data.net && data.net.estimate ? data.net.estimate : null;

  return (
    <div style={{ margin: "16px 0" }} data-testid="open-pipeline">
      <div className="sec-head" style={{ fontSize: 15 }}>Open pipeline</div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, margin: "10px 0" }}>
        <Headline label="Open pipeline" value={money(data.open_total)}
          note={`${deals} ${deals === 1 ? "deal" : "deals"}, ${blank} with no amount yet`} />
        <Headline label="Weighted" value={money(data.weighted_total)} />
        {ds && (
          <>
            <Headline label="DS expected" value={money(ds.expected)}
              note={ds.default_count ? `${ds.default_count} at the typical ${money(ds.default_size)}` : null} />
            <Headline label="DS weighted" value={money(ds.weighted)}
              note={ds.default_count ? "typical-size deals at half odds" : null} />
          </>
        )}
      </div>

      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr>
              {["Stage", "Deals", "Total", "No amount yet"].map((h, i) => (
                <th key={h} style={{ ...cell, textAlign: i ? "right" : "left", color: DIM }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {stages.map((s) => (
              <tr key={s.id}>
                <td style={cell}>{s.label}</td>
                <td style={num}>{s.count}</td>
                <td style={num}>{money(s.total)}</td>
                <td style={num}>{s.blank}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {sources.length > 0 && (
        <div style={{ fontSize: 12, color: DIM, margin: "8px 0" }}>
          By source: {sources.map(([k, s]) => `${SOURCE_LABELS[k] || k} ${s.count} (${money(s.total)})`).join(" · ")}
        </div>
      )}

      {needsLook.length > 0 && (
        <div style={{ margin: "14px 0" }} data-testid="open-pipeline-needs-a-look">
          <div className="sec-head" style={{ fontSize: 13 }}>Needs a look</div>
          <div style={{ fontSize: 12, color: DIM, marginBottom: 4 }}>
            Invoices that could belong to more than one open deal. Pick the right one in HubSpot.
          </div>
          {needsLook.map((n) => (
            <div key={n.invoice_key} style={{ fontSize: 13, padding: "4px 0", borderBottom: LINE }}>
              {n.client} · invoice {n.doc}
              {(n.deal_urls || []).map((u, i) => (
                <span key={u}> · <a href={u} target="_blank" rel="noreferrer" style={link}>Deal {i + 1}</a></span>
              ))}
            </div>
          ))}
        </div>
      )}

      {quiet.length > 0 && (
        <div style={{ margin: "14px 0" }} data-testid="open-pipeline-gone-quiet">
          <div className="sec-head" style={{ fontSize: 13 }}>Gone quiet</div>
          <div style={{ fontSize: 12, color: DIM, marginBottom: 4 }}>
            No activity for over 90 days, oldest first.
          </div>
          {quiet.map((g) => (
            <div key={g.id} style={{ fontSize: 13, padding: "4px 0", borderBottom: LINE }}>
              <a href={g.url} target="_blank" rel="noreferrer" style={link}>{g.name || `Deal ${g.id}`}</a>
              {" "}· {g.stage} · last activity {String(g.last_activity).slice(0, 10)}
            </div>
          ))}
        </div>
      )}

      {est && (
        <div style={{ margin: "14px 0" }} data-testid="open-pipeline-net">
          <div className="sec-head" style={{ fontSize: 13 }}>Estimated net revenue</div>
          <div style={{ fontSize: 12, color: DIM, marginBottom: 4 }}>
            What DS keeps after paying the athlete, estimated: everything invoiced, times the
            average margin on won deals where both the client fee and the athlete fee are known.
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
            <Headline label="Average margin" value={`${est.rate_pct}%`}
              note="where both fees are known" />
            <Headline label={`Est. net, ${data.net.year}`} value={money(est.this_year.net)}
              note={`on ${money(est.this_year.invoiced)} invoiced`} />
            <Headline label="Est. net, all time" value={money(est.all_time.net)}
              note={`on ${money(est.all_time.invoiced)} invoiced`} />
          </div>
        </div>
      )}

      <div style={{ fontSize: 11, color: DIM }}>
        From HubSpot, as of {data.at ? new Date(data.at).toLocaleString() : "an earlier sync"}.
        Changes made in the last few minutes may show on the next sync.
      </div>
    </div>
  );
}
